import { useState, useEffect, useRef } from 'react';

interface CourseFeature {
  title: string;
  desc: string;
}

interface Course {
  id: number;
  name: string;
  duration: string;
  price: number;
  level: string;
  description: string;
  image: string;
  images?: string[];
  includes: string[];
  features: CourseFeature[];
  dates: string[];
  tags: string[];
}

interface CourseDetailOverlayProps {
  course: Course;
  themeIndex: number;
  onClose: () => void;
  onBook: (courseName: string) => void;
}

const COURSE_THEMES = [
  { bg: '#1a1208', accent: '#c9a96e', light: '#f5ede0', muted: 'rgba(201,169,110,0.15)' },
  { bg: '#0d1a14', accent: '#6db88a', light: '#e0f5ea', muted: 'rgba(109,184,138,0.15)' },
  { bg: '#160d1a', accent: '#b86db8', light: '#f0e0f5', muted: 'rgba(184,109,184,0.15)' },
];

const SECTION_ICONS = [
  'ri-compass-3-line',
  'ri-flask-line',
  'ri-heart-pulse-line',
  'ri-star-line',
  'ri-calendar-event-line',
  'ri-award-line',
];

function useScrollAnimation(ref: React.RefObject<HTMLElement | null>, threshold = 0.25) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [ref, threshold]);
  return visible;
}

// 區塊 1：課程概覽 Hero
function Section1Hero({ course, theme, onBook }: { course: Course; theme: typeof COURSE_THEMES[0]; onBook: () => void }) {
  const [loaded, setLoaded] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const allImages = (course.images && course.images.length > 0) ? course.images : [course.image];

  useEffect(() => { setTimeout(() => setLoaded(true), 80); }, []);

  // 多張圖時每 4 秒自動切換
  useEffect(() => {
    if (allImages.length <= 1) return;
    const timer = setInterval(() => {
      setActiveImg(prev => (prev + 1) % allImages.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [allImages.length]);

  return (
    <section
      className="relative min-h-screen flex items-center overflow-hidden"
      style={{ background: theme.bg }}
    >
      {/* 背景圖片（支援多圖切換） */}
      <div className="absolute inset-0">
        {allImages.map((img, i) => (
          <img
            key={i}
            src={img}
            alt={course.name}
            className="absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-1000"
            style={{
              opacity: i === activeImg ? (loaded ? 0.35 : 0) : 0,
              transform: loaded ? 'scale(1)' : 'scale(1.1)',
              transition: i === activeImg
                ? 'opacity 1s ease, transform 1.8s cubic-bezier(0.4,0,0.2,1)'
                : 'opacity 1s ease',
            }}
          />
        ))}
        <div className="absolute inset-0" style={{ background: `linear-gradient(to right, ${theme.bg}f0 45%, ${theme.bg}80 100%)` }} />
        <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${theme.bg} 0%, transparent 50%)` }} />
      </div>

      {/* 大型背景文字 */}
      <div
        className="absolute right-0 top-1/2 -translate-y-1/2 select-none pointer-events-none"
        style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: 'clamp(120px, 18vw, 280px)',
          fontWeight: 900,
          color: 'rgba(255,255,255,0.03)',
          lineHeight: 1,
          opacity: loaded ? 1 : 0,
          transition: 'opacity 1.5s ease 0.5s',
        }}
      >
        {String(course.id).padStart(2, '0')}
      </div>

      <div className="relative z-10 w-full max-w-6xl mx-auto px-8 lg:px-20 py-32">
        {/* 標籤列 */}
        <div
          className="flex items-center gap-4 mb-8"
          style={{
            opacity: loaded ? 1 : 0,
            transform: loaded ? 'translateY(0)' : 'translateY(30px)',
            transition: 'opacity 0.8s ease 0.2s, transform 0.8s ease 0.2s',
          }}
        >
          <span className="text-xs tracking-[0.4em] uppercase font-semibold" style={{ color: theme.accent }}>
            Workshop Course
          </span>
          <div className="h-px w-16" style={{ background: `${theme.accent}60` }} />
          <span
            className="px-3 py-1 rounded-full text-xs font-semibold"
            style={{ background: theme.muted, color: theme.accent, border: `1px solid ${theme.accent}40` }}
          >
            {course.level}
          </span>
        </div>

        {/* 課程名稱 */}
        <h1
          className="text-6xl lg:text-8xl font-bold text-white mb-6 leading-none"
          style={{
            fontFamily: "'Playfair Display', serif",
            opacity: loaded ? 1 : 0,
            transform: loaded ? 'translateY(0)' : 'translateY(60px)',
            transition: 'opacity 1s ease 0.35s, transform 1s ease 0.35s',
          }}
        >
          {course.name}
        </h1>

        {/* 描述 */}
        <p
          className="text-white/60 text-xl max-w-xl leading-relaxed mb-10"
          style={{
            opacity: loaded ? 1 : 0,
            transform: loaded ? 'translateY(0)' : 'translateY(40px)',
            transition: 'opacity 0.9s ease 0.5s, transform 0.9s ease 0.5s',
          }}
        >
          {course.description}
        </p>

        {/* 資訊列 */}
        <div
          className="flex flex-wrap items-center gap-8 mb-12"
          style={{
            opacity: loaded ? 1 : 0,
            transform: loaded ? 'translateY(0)' : 'translateY(30px)',
            transition: 'opacity 0.8s ease 0.65s, transform 0.8s ease 0.65s',
          }}
        >
          {[
            { icon: 'ri-time-line', label: '課程時長', value: course.duration },
            { icon: 'ri-group-line', label: '班級人數', value: '小班制 6 人' },
            { icon: 'ri-money-cny-circle-line', label: '課程費用', value: `NT$ ${course.price.toLocaleString()}` },
          ].map((info, i) => (
            <div key={i} className="flex items-center gap-3">
              <div
                className="w-10 h-10 flex items-center justify-center rounded-full"
                style={{ background: theme.muted, border: `1px solid ${theme.accent}40` }}
              >
                <i className={`${info.icon} text-base`} style={{ color: theme.accent }}></i>
              </div>
              <div>
                <p className="text-white/40 text-xs">{info.label}</p>
                <p className="text-white text-sm font-semibold">{info.value}</p>
              </div>
            </div>
          ))}
        </div>

        {/* 標籤 */}
        <div
          className="flex flex-wrap gap-2 mb-12"
          style={{
            opacity: loaded ? 1 : 0,
            transform: loaded ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.8s ease 0.8s, transform 0.8s ease 0.8s',
          }}
        >
          {course.tags.map((tag, i) => (
            <span
              key={i}
              className="px-4 py-1.5 rounded-full text-xs font-medium"
              style={{ background: theme.muted, border: `1px solid ${theme.accent}40`, color: theme.accent }}
            >
              {tag}
            </span>
          ))}
        </div>

        {/* CTA */}
        <div
          className="flex items-center gap-4"
          style={{
            opacity: loaded ? 1 : 0,
            transform: loaded ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.8s ease 0.95s, transform 0.8s ease 0.95s',
          }}
        >
          <button
            onClick={onBook}
            className="group flex items-center gap-3 px-10 py-4 rounded-full font-medium text-sm tracking-wide text-white transition-all duration-300 cursor-pointer whitespace-nowrap"
            style={{ background: theme.accent }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'scale(1.05)'; (e.currentTarget as HTMLElement).style.boxShadow = `0 12px 40px ${theme.accent}60`; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = 'scale(1)'; (e.currentTarget as HTMLElement).style.boxShadow = 'none'; }}
          >
            立即預約
            <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1"></i>
          </button>
        </div>
      </div>

      {/* 多圖縮圖列 */}
      {allImages.length > 1 && (
        <div
          className="absolute bottom-24 right-8 lg:right-20 flex flex-col gap-2 z-20"
          style={{
            opacity: loaded ? 1 : 0,
            transition: 'opacity 1s ease 1.1s',
          }}
        >
          {allImages.map((img, i) => (
            <button
              key={i}
              onClick={() => setActiveImg(i)}
              className="relative rounded-lg overflow-hidden cursor-pointer transition-all duration-300"
              style={{
                width: '72px',
                height: '52px',
                border: `2px solid ${i === activeImg ? theme.accent : 'rgba(255,255,255,0.2)'}`,
                opacity: i === activeImg ? 1 : 0.55,
                transform: i === activeImg ? 'scale(1.08)' : 'scale(1)',
                boxShadow: i === activeImg ? `0 4px 16px ${theme.accent}60` : 'none',
              }}
            >
              <img src={img} alt={`課程圖片 ${i + 1}`} className="w-full h-full object-cover object-top" />
              {i === 0 && (
                <div
                  className="absolute bottom-0 left-0 right-0 text-center text-[9px] font-semibold"
                  style={{ background: theme.accent, color: '#fff', lineHeight: '14px' }}
                >
                  主圖
                </div>
              )}
            </button>
          ))}
          <div className="text-center mt-1">
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
              {activeImg + 1}/{allImages.length}
            </span>
          </div>
        </div>
      )}

      {/* 向下滾動提示 */}
      <div
        className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
        style={{
          opacity: loaded ? 1 : 0,
          transition: 'opacity 1s ease 1.2s',
        }}
      >
        <span className="text-white/30 text-xs tracking-[0.3em] uppercase">Scroll to Explore</span>
        <div className="w-px h-12 relative overflow-hidden" style={{ background: 'rgba(255,255,255,0.1)' }}>
          <div
            className="absolute top-0 left-0 w-full"
            style={{ height: '40%', background: theme.accent, animation: 'detailScrollLine 1.8s ease-in-out infinite' }}
          />
        </div>
      </div>
    </section>
  );
}

// 區塊 2：課程特色深度介紹
function Section2Features({ course, theme }: { course: Course; theme: typeof COURSE_THEMES[0] }) {
  const ref = useRef<HTMLElement>(null);
  const visible = useScrollAnimation(ref as React.RefObject<HTMLElement>);
  const [activeFeature, setActiveFeature] = useState(0);

  return (
    <section
      ref={ref}
      className="relative min-h-screen flex items-center overflow-hidden"
      style={{ background: '#0a0a0a' }}
    >
      {/* 左側裝飾線 */}
      <div
        className="absolute left-0 top-0 bottom-0 w-1"
        style={{
          background: `linear-gradient(to bottom, transparent, ${theme.accent}, transparent)`,
          opacity: visible ? 1 : 0,
          transition: 'opacity 1s ease 0.3s',
        }}
      />

      <div className="w-full max-w-6xl mx-auto px-8 lg:px-20 py-24">
        {/* 標題 */}
        <div
          className="mb-16"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(40px)',
            transition: 'opacity 0.8s ease 0.1s, transform 0.8s ease 0.1s',
          }}
        >
          <p className="text-xs tracking-[0.5em] uppercase font-semibold mb-4" style={{ color: theme.accent }}>
            Course Features
          </p>
          <h2
            className="text-5xl lg:text-7xl font-bold text-white leading-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            課程特色
          </h2>
        </div>

        <div className="flex flex-col lg:flex-row gap-12 items-start">
          {/* 左側：特色列表 */}
          <div className="w-full lg:w-2/5 space-y-2">
            {course.features.map((feature, i) => (
              <button
                key={i}
                onClick={() => setActiveFeature(i)}
                className="w-full text-left p-5 rounded-2xl transition-all duration-400 cursor-pointer"
                style={{
                  background: activeFeature === i ? theme.muted : 'transparent',
                  border: `1px solid ${activeFeature === i ? theme.accent + '60' : 'rgba(255,255,255,0.06)'}`,
                  opacity: visible ? 1 : 0,
                  transform: visible ? 'translateX(0)' : 'translateX(-40px)',
                  transition: `opacity 0.6s ease ${0.2 + i * 0.1}s, transform 0.6s ease ${0.2 + i * 0.1}s, background 0.3s ease, border-color 0.3s ease`,
                }}
              >
                <div className="flex items-center gap-4">
                  <div
                    className="w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0"
                    style={{
                      background: activeFeature === i ? theme.accent : 'rgba(255,255,255,0.05)',
                      transition: 'background 0.3s ease',
                    }}
                  >
                    <i
                      className={`${SECTION_ICONS[i % SECTION_ICONS.length]} text-sm`}
                      style={{ color: activeFeature === i ? '#fff' : theme.accent }}
                    ></i>
                  </div>
                  <span
                    className="font-semibold text-sm"
                    style={{ color: activeFeature === i ? '#fff' : 'rgba(255,255,255,0.6)' }}
                  >
                    {feature.title}
                  </span>
                  <i
                    className="ri-arrow-right-s-line ml-auto"
                    style={{
                      color: theme.accent,
                      opacity: activeFeature === i ? 1 : 0,
                      transform: activeFeature === i ? 'translateX(0)' : 'translateX(-8px)',
                      transition: 'opacity 0.3s ease, transform 0.3s ease',
                    }}
                  ></i>
                </div>
              </button>
            ))}
          </div>

          {/* 右側：詳細說明 */}
          <div
            className="w-full lg:w-3/5"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateX(0)' : 'translateX(60px)',
              transition: 'opacity 0.9s ease 0.4s, transform 0.9s ease 0.4s',
            }}
          >
            <div
              className="rounded-3xl p-10 h-full min-h-[320px] flex flex-col justify-between"
              style={{ background: theme.muted, border: `1px solid ${theme.accent}30` }}
            >
              <div>
                <div
                  className="w-16 h-16 flex items-center justify-center rounded-2xl mb-8"
                  style={{ background: theme.accent }}
                >
                  <i
                    className={`${SECTION_ICONS[activeFeature % SECTION_ICONS.length]} text-2xl text-white`}
                  ></i>
                </div>
                <h3
                  className="text-3xl font-bold text-white mb-4"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {course.features[activeFeature]?.title}
                </h3>
                <p className="text-white/60 text-lg leading-relaxed">
                  {course.features[activeFeature]?.desc}
                </p>
              </div>
              <div className="flex items-center gap-2 mt-8">
                {course.features.map((_, i) => (
                  <div
                    key={i}
                    className="rounded-full transition-all duration-300"
                    style={{
                      width: activeFeature === i ? '24px' : '6px',
                      height: '6px',
                      background: activeFeature === i ? theme.accent : 'rgba(255,255,255,0.2)',
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// 區塊 3：課程包含 — 視覺化展示
function Section3Includes({ course, theme }: { course: Course; theme: typeof COURSE_THEMES[0] }) {
  const ref = useRef<HTMLElement>(null);
  const visible = useScrollAnimation(ref as React.RefObject<HTMLElement>);

  const includeIcons = ['ri-box-3-line', 'ri-leaf-line', 'ri-palette-line', 'ri-customer-service-2-line', 'ri-shield-check-line', 'ri-gift-2-line'];

  return (
    <section
      ref={ref}
      className="relative min-h-screen flex items-center overflow-hidden"
      style={{ background: theme.bg }}
    >
      {/* 背景模糊圖 */}
      <div className="absolute inset-0 opacity-20">
        <img src={course.image} alt="" className="w-full h-full object-cover" style={{ filter: 'blur(60px)', transform: 'scale(1.2)' }} />
      </div>
      <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.5)' }} />

      {/* 大型裝飾文字 */}
      <div
        className="absolute bottom-0 right-0 select-none pointer-events-none"
        style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: 'clamp(100px, 15vw, 220px)',
          fontWeight: 900,
          color: 'rgba(255,255,255,0.025)',
          lineHeight: 1,
          opacity: visible ? 1 : 0,
          transition: 'opacity 1.5s ease 0.5s',
        }}
      >
        INCLUDES
      </div>

      <div className="relative z-10 w-full max-w-6xl mx-auto px-8 lg:px-20 py-24">
        <div
          className="mb-16"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(40px)',
            transition: 'opacity 0.8s ease 0.1s, transform 0.8s ease 0.1s',
          }}
        >
          <p className="text-xs tracking-[0.5em] uppercase font-semibold mb-4" style={{ color: theme.accent }}>
            What's Included
          </p>
          <h2
            className="text-5xl lg:text-7xl font-bold text-white leading-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            課程包含
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {course.includes.map((item, i) => (
            <div
              key={i}
              className="flex items-center gap-6 p-6 rounded-2xl"
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: `1px solid ${theme.accent}25`,
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0) scale(1)' : 'translateY(50px) scale(0.95)',
                transition: `opacity 0.7s ease ${0.2 + i * 0.12}s, transform 0.7s ease ${0.2 + i * 0.12}s`,
              }}
            >
              <div
                className="w-14 h-14 flex items-center justify-center rounded-2xl flex-shrink-0"
                style={{ background: theme.muted, border: `1px solid ${theme.accent}40` }}
              >
                <i className={`${includeIcons[i % includeIcons.length]} text-2xl`} style={{ color: theme.accent }}></i>
              </div>
              <div>
                <p className="text-white font-semibold text-base">{item}</p>
                <p className="text-white/40 text-sm mt-1">課程提供</p>
              </div>
              <div className="ml-auto">
                <i className="ri-check-double-line text-xl" style={{ color: theme.accent }}></i>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// 區塊 4：課程流程時間軸
function Section4Timeline({ course, theme }: { course: Course; theme: typeof COURSE_THEMES[0] }) {
  const ref = useRef<HTMLElement>(null);
  const visible = useScrollAnimation(ref as React.RefObject<HTMLElement>);

  const timeline = [
    { time: '開始', title: '歡迎與介紹', desc: '認識講師與同學，了解今日課程流程與安全須知', icon: 'ri-door-open-line' },
    { time: '前段', title: '理論基礎', desc: '深入了解香氛科學、原料特性與調配原理', icon: 'ri-book-open-line' },
    { time: '中段', title: '示範教學', desc: '講師親自示範調配技巧，解說每個步驟的要點', icon: 'ri-eye-line' },
    { time: '後段', title: '實作體驗', desc: '親手調配屬於自己的香氛作品，講師全程指導', icon: 'ri-hand-heart-line' },
    { time: '尾聲', title: '成果分享', desc: '展示作品、拍照留念，分享今日的學習心得', icon: 'ri-camera-line' },
    { time: '結束', title: '帶回成品', desc: '包裝成品、取得課程資料，課後諮詢服務說明', icon: 'ri-gift-2-line' },
  ];

  return (
    <section
      ref={ref}
      className="relative min-h-screen flex items-center overflow-hidden"
      style={{ background: '#080808' }}
    >
      <div className="w-full max-w-5xl mx-auto px-8 lg:px-20 py-24">
        <div
          className="mb-16"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(40px)',
            transition: 'opacity 0.8s ease 0.1s, transform 0.8s ease 0.1s',
          }}
        >
          <p className="text-xs tracking-[0.5em] uppercase font-semibold mb-4" style={{ color: theme.accent }}>
            Course Timeline
          </p>
          <h2
            className="text-5xl lg:text-7xl font-bold text-white leading-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            課程流程
          </h2>
        </div>

        <div className="relative">
          {/* 中央連接線 */}
          <div
            className="absolute left-8 top-0 bottom-0 w-px"
            style={{
              background: `linear-gradient(to bottom, transparent, ${theme.accent}60, transparent)`,
              opacity: visible ? 1 : 0,
              transition: 'opacity 1s ease 0.3s',
            }}
          />

          <div className="space-y-8">
            {timeline.map((step, i) => (
              <div
                key={i}
                className="flex items-start gap-8 pl-4"
                style={{
                  opacity: visible ? 1 : 0,
                  transform: visible ? 'translateX(0)' : 'translateX(-50px)',
                  transition: `opacity 0.7s ease ${0.2 + i * 0.1}s, transform 0.7s ease ${0.2 + i * 0.1}s`,
                }}
              >
                {/* 節點 */}
                <div className="relative flex-shrink-0">
                  <div
                    className="w-8 h-8 flex items-center justify-center rounded-full z-10 relative"
                    style={{ background: theme.accent, boxShadow: `0 0 20px ${theme.accent}60` }}
                  >
                    <i className={`${step.icon} text-sm text-white`}></i>
                  </div>
                </div>

                {/* 內容 */}
                <div
                  className="flex-1 p-6 rounded-2xl mb-2"
                  style={{
                    background: 'rgba(255,255,255,0.03)',
                    border: `1px solid rgba(255,255,255,0.06)`,
                  }}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <span
                      className="text-xs font-bold tracking-widest uppercase px-2 py-0.5 rounded"
                      style={{ background: theme.muted, color: theme.accent }}
                    >
                      {step.time}
                    </span>
                    <h4 className="text-white font-semibold text-base">{step.title}</h4>
                  </div>
                  <p className="text-white/50 text-sm leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// 區塊 5：可預約日期
function Section5Dates({ course, theme, onBook }: { course: Course; theme: typeof COURSE_THEMES[0]; onBook: () => void }) {
  const ref = useRef<HTMLElement>(null);
  const visible = useScrollAnimation(ref as React.RefObject<HTMLElement>);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    const weekdays = ['日', '一', '二', '三', '四', '五', '六'];
    return {
      month: d.getMonth() + 1,
      day: d.getDate(),
      weekday: weekdays[d.getDay()],
      full: `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()}`,
    };
  };

  return (
    <section
      ref={ref}
      className="relative min-h-screen flex items-center overflow-hidden"
      style={{ background: theme.bg }}
    >
      {/* 右側大型裝飾 */}
      <div
        className="absolute right-0 top-1/2 -translate-y-1/2 select-none pointer-events-none"
        style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: 'clamp(120px, 18vw, 260px)',
          fontWeight: 900,
          color: 'rgba(255,255,255,0.025)',
          lineHeight: 1,
          opacity: visible ? 1 : 0,
          transition: 'opacity 1.5s ease 0.5s',
        }}
      >
        DATES
      </div>

      <div className="relative z-10 w-full max-w-6xl mx-auto px-8 lg:px-20 py-24">
        <div
          className="mb-16"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(40px)',
            transition: 'opacity 0.8s ease 0.1s, transform 0.8s ease 0.1s',
          }}
        >
          <p className="text-xs tracking-[0.5em] uppercase font-semibold mb-4" style={{ color: theme.accent }}>
            Available Dates
          </p>
          <h2
            className="text-5xl lg:text-7xl font-bold text-white leading-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            可預約日期
          </h2>
          <p className="text-white/40 text-base mt-4">選擇您方便的日期，立即預約課程</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {course.dates.map((date, i) => {
            const d = formatDate(date);
            const isSelected = selectedDate === date;
            return (
              <button
                key={i}
                onClick={() => setSelectedDate(isSelected ? null : date)}
                className="p-6 rounded-2xl text-center transition-all duration-300 cursor-pointer"
                style={{
                  background: isSelected ? theme.accent : 'rgba(255,255,255,0.04)',
                  border: `1px solid ${isSelected ? theme.accent : 'rgba(255,255,255,0.08)'}`,
                  transform: isSelected ? 'scale(1.05)' : 'scale(1)',
                  boxShadow: isSelected ? `0 12px 40px ${theme.accent}40` : 'none',
                  opacity: visible ? 1 : 0,
                  transition: `opacity 0.6s ease ${0.2 + i * 0.1}s, transform 0.3s ease, background 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease`,
                }}
              >
                <p
                  className="text-xs font-semibold tracking-widest mb-1"
                  style={{ color: isSelected ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.3)' }}
                >
                  {d.month}月
                </p>
                <p
                  className="text-4xl font-bold leading-none mb-1"
                  style={{
                    fontFamily: "'Playfair Display', serif",
                    color: isSelected ? '#fff' : 'rgba(255,255,255,0.8)',
                  }}
                >
                  {d.day}
                </p>
                <p
                  className="text-xs"
                  style={{ color: isSelected ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.3)' }}
                >
                  週{d.weekday}
                </p>
              </button>
            );
          })}
        </div>

        {/* 選擇後顯示 */}
        <div
          className="p-6 rounded-2xl mb-8 transition-all duration-500"
          style={{
            background: selectedDate ? theme.muted : 'rgba(255,255,255,0.02)',
            border: `1px solid ${selectedDate ? theme.accent + '50' : 'rgba(255,255,255,0.05)'}`,
            opacity: visible ? 1 : 0,
            transition: 'opacity 0.8s ease 0.6s, background 0.4s ease, border-color 0.4s ease',
          }}
        >
          {selectedDate ? (
            <div className="flex items-center gap-4">
              <i className="ri-calendar-check-line text-2xl" style={{ color: theme.accent }}></i>
              <div>
                <p className="text-white font-semibold">已選擇：{formatDate(selectedDate).full}（週{formatDate(selectedDate).weekday}）</p>
                <p className="text-white/50 text-sm mt-0.5">點擊下方按鈕完成預約</p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-4">
              <i className="ri-calendar-line text-2xl" style={{ color: 'rgba(255,255,255,0.2)' }}></i>
              <p className="text-white/30 text-sm">請從上方選擇您方便的日期</p>
            </div>
          )}
        </div>

        <div
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.8s ease 0.7s, transform 0.8s ease 0.7s',
          }}
        >
          <button
            onClick={onBook}
            className="flex items-center gap-3 px-10 py-4 rounded-full font-medium text-sm tracking-wide text-white transition-all duration-300 cursor-pointer whitespace-nowrap"
            style={{
              background: selectedDate ? theme.accent : 'rgba(255,255,255,0.1)',
              opacity: selectedDate ? 1 : 0.5,
            }}
            onMouseEnter={e => { if (selectedDate) { (e.currentTarget as HTMLElement).style.transform = 'scale(1.05)'; } }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = 'scale(1)'; }}
          >
            {selectedDate ? '確認預約此日期' : '選擇日期後預約'}
            <i className="ri-arrow-right-line"></i>
          </button>
        </div>
      </div>
    </section>
  );
}

// 區塊 6：最終 CTA — 沉浸式收尾
function Section6CTA({ course, theme, onBook }: { course: Course; theme: typeof COURSE_THEMES[0]; onBook: () => void }) {
  const ref = useRef<HTMLElement>(null);
  const visible = useScrollAnimation(ref as React.RefObject<HTMLElement>);

  return (
    <section
      ref={ref}
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{ background: '#050505' }}
    >
      {/* 背景圖 */}
      <div className="absolute inset-0">
        <img
          src={course.image}
          alt=""
          className="w-full h-full object-cover object-center"
          style={{
            transform: visible ? 'scale(1)' : 'scale(1.1)',
            transition: 'transform 1.8s cubic-bezier(0.4,0,0.2,1)',
            opacity: 0.3,
          }}
        />
        <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.7)' }} />
        <div
          className="absolute inset-0"
          style={{ background: `radial-gradient(ellipse at center, ${theme.accent}15 0%, transparent 60%)` }}
        />
      </div>

      <div className="relative z-10 text-center px-8 max-w-3xl mx-auto">
        {/* 裝飾圓圈 */}
        <div
          className="w-24 h-24 flex items-center justify-center rounded-full mx-auto mb-10"
          style={{
            background: theme.muted,
            border: `1px solid ${theme.accent}50`,
            opacity: visible ? 1 : 0,
            transform: visible ? 'scale(1)' : 'scale(0.5)',
            transition: 'opacity 0.8s ease 0.1s, transform 0.8s cubic-bezier(0.34,1.56,0.64,1) 0.1s',
          }}
        >
          <i className="ri-leaf-line text-4xl" style={{ color: theme.accent }}></i>
        </div>

        <p
          className="text-xs tracking-[0.5em] uppercase font-semibold mb-6"
          style={{
            color: theme.accent,
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.8s ease 0.25s, transform 0.8s ease 0.25s',
          }}
        >
          Ready to Begin
        </p>

        <h2
          className="text-6xl lg:text-8xl font-bold text-white mb-6 leading-tight"
          style={{
            fontFamily: "'Playfair Display', serif",
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(50px)',
            transition: 'opacity 1s ease 0.35s, transform 1s ease 0.35s',
          }}
        >
          {course.name}
        </h2>

        <p
          className="text-white/50 text-lg mb-4 leading-relaxed"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'opacity 0.8s ease 0.5s, transform 0.8s ease 0.5s',
          }}
        >
          {course.description}
        </p>

        {/* 價格 */}
        <div
          className="inline-flex items-baseline gap-2 mb-12"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.8s ease 0.6s, transform 0.8s ease 0.6s',
          }}
        >
          <span className="text-white/40 text-sm">課程費用</span>
          <span
            className="text-5xl font-bold"
            style={{ fontFamily: "'Playfair Display', serif", color: theme.accent }}
          >
            NT$ {course.price.toLocaleString()}
          </span>
        </div>

        {/* 按鈕組 */}
        <div
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(30px)',
            transition: 'opacity 0.8s ease 0.75s, transform 0.8s ease 0.75s',
          }}
        >
          <button
            onClick={onBook}
            className="group flex items-center gap-3 px-12 py-5 rounded-full font-medium text-white text-sm tracking-widest uppercase transition-all duration-300 cursor-pointer whitespace-nowrap"
            style={{ background: theme.accent }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'scale(1.05)'; (e.currentTarget as HTMLElement).style.boxShadow = `0 16px 50px ${theme.accent}60`; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = 'scale(1)'; (e.currentTarget as HTMLElement).style.boxShadow = 'none'; }}
          >
            立即預約課程
            <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1"></i>
          </button>
        </div>
      </div>
    </section>
  );
}

// 側邊進度導航
function SideNav({ theme, activeSection }: { theme: typeof COURSE_THEMES[0]; activeSection: number }) {
  const sections = ['課程概覽', '課程特色', '課程包含', '課程流程', '預約日期', '立即預約'];
  return (
    <div className="fixed right-8 top-1/2 -translate-y-1/2 z-[200] flex flex-col gap-3">
      {sections.map((label, i) => (
        <button
          key={i}
          onClick={() => {
            document.getElementById(`detail-section-${i}`)?.scrollIntoView({ behavior: 'smooth' });
          }}
          className="group flex items-center gap-3 cursor-pointer"
          title={label}
        >
          <span
            className="text-xs font-medium whitespace-nowrap transition-all duration-300"
            style={{
              color: activeSection === i ? theme.accent : 'rgba(255,255,255,0.3)',
              opacity: activeSection === i ? 1 : 0,
              transform: activeSection === i ? 'translateX(0)' : 'translateX(8px)',
            }}
          >
            {label}
          </span>
          <div
            className="rounded-full transition-all duration-300 flex-shrink-0"
            style={{
              width: activeSection === i ? '10px' : '6px',
              height: activeSection === i ? '10px' : '6px',
              background: activeSection === i ? theme.accent : 'rgba(255,255,255,0.25)',
              boxShadow: activeSection === i ? `0 0 8px ${theme.accent}80` : 'none',
            }}
          />
        </button>
      ))}
    </div>
  );
}

export default function CourseDetailOverlay({ course, themeIndex, onClose, onBook }: CourseDetailOverlayProps) {
  const theme = COURSE_THEMES[themeIndex % COURSE_THEMES.length];
  const [visible, setVisible] = useState(false);
  const [activeSection, setActiveSection] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setTimeout(() => setVisible(true), 10);
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // ESC 關閉
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // 偵測目前在哪個區塊
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const handleScroll = () => {
      for (let i = 5; i >= 0; i--) {
        const el = document.getElementById(`detail-section-${i}`);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= window.innerHeight * 0.5) {
            setActiveSection(i);
            break;
          }
        }
      }
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 500);
  };

  const handleBook = () => {
    handleClose();
    setTimeout(() => onBook(course.name), 500);
  };

  return (
    <div
      className="fixed inset-0 z-[150]"
      style={{
        opacity: visible ? 1 : 0,
        transition: 'opacity 0.5s ease',
      }}
    >
      {/* 關閉按鈕 */}
      <button
        onClick={handleClose}
        className="fixed top-6 left-6 z-[210] flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap"
        style={{
          background: 'rgba(255,255,255,0.1)',
          border: '1px solid rgba(255,255,255,0.2)',
          color: 'rgba(255,255,255,0.8)',
          backdropFilter: 'blur(10px)',
        }}
        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.2)'; }}
        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.1)'; }}
      >
        <i className="ri-arrow-left-line"></i>
        返回課程列表
      </button>

      {/* 側邊導航 */}
      <SideNav theme={theme} activeSection={activeSection} />

      {/* 可滾動內容 */}
      <div
        ref={containerRef}
        className="w-full h-full overflow-y-auto"
        style={{ scrollBehavior: 'smooth' }}
      >
        <div id="detail-section-0"><Section1Hero course={course} theme={theme} onBook={handleBook} /></div>
        <div id="detail-section-1"><Section2Features course={course} theme={theme} /></div>
        <div id="detail-section-2"><Section3Includes course={course} theme={theme} /></div>
        <div id="detail-section-3"><Section4Timeline course={course} theme={theme} /></div>
        <div id="detail-section-4"><Section5Dates course={course} theme={theme} onBook={handleBook} /></div>
        <div id="detail-section-5"><Section6CTA course={course} theme={theme} onBook={handleBook} /></div>
      </div>

      <style>{`
        @keyframes detailScrollLine {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(350%); }
        }
      `}</style>
    </div>
  );
}
